import {ProductInterface, CustomComponentsInterface} from "@/core/types";
import {config} from "@/core/config";

import {searchErrorLogger, searchLogger} from './Logger'

import {SmartSearchConfig} from "@/main";
import {AVAILABLE_CURRENCIES} from "@/core/Currencies";
import DOMPurify from "dompurify";


// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function validatePriceFormat(value) {
  const regexp = new RegExp("^[0-9]+(.[0-9]{1,2})?$", "m");
  return regexp.exec(value) !== null;
}

export function SanitizeValue(value: string) {
  
  const settings = {
    ALLOWED_TAGS: [''],
    ALLOWED_ATTR: [''],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false
  }
  return DOMPurify.sanitize(value, settings)
}

export function parsePrice(price: number | string | null, decimalSeparator = ".\\,"): number | null {
  if (price === '') {
    return null
  }
  if (typeof price === "string") {
    const re = new RegExp("[^0-9\\" + decimalSeparator + "]+", "g");
    const parsedPrice = price.replace(re, "").replace(/[^0-9]/g, ".");
    if (validatePriceFormat(parsedPrice)) {
      return Number(parsedPrice);
    } else {
      searchErrorLogger.log(`Error in q.parsePrice: Price: "${price}" is not valid!`);
      return null
    }
  }
  return price;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const formatPrice = (price, currency = 'PLN', removeDecimals = true, unit = false): string => {
  price = parsePrice(price)
  if (!price) {
    return ''
  }
  if (!currency) {
    return price
  }
  price = typeof price === 'number' ? price : Number(price.replace(',', '.'))
  
  if (unit) {
    return price
  }
  
  if (AVAILABLE_CURRENCIES[currency]) {
    price = price.toLocaleString(AVAILABLE_CURRENCIES[currency].locale, {
      currency: AVAILABLE_CURRENCIES[currency].currency,
      style: 'currency',
    });
  }
  
  if (removeDecimals) {
    price = price.replace(',00', '').replace('.00', '')
  }
  return price
}

const bytesToBase64 = (bytes: Uint8Array) => {
  const binString = Array.from(bytes, (byte) =>
    String.fromCodePoint(byte),
  ).join("");
  return btoa(binString);
}

export const generateStaticImage = (imgUrl: string, width = 300, height = 300) => {
  const customerSymbol = config.customerSymbol
  imgUrl = imgUrl.split('?')[0]
  let burl = bytesToBase64(new TextEncoder().encode(imgUrl))
  burl = burl.replace(/\//g, '_')
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return `https://qon-csts3.quartic.com.pl/${customerSymbol}/e1-c0/${width}x${height}/${burl.match(/.{1,32}/g).join('/')}.webp`
}

export const addDisplayMacros = (variant: ProductInterface) => {
  for (const property in variant) {
    const propertyName = `has${property.toString()[0].toUpperCase()}${property.slice(1)}`
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    variant[propertyName] = variant[property] ? 'q-d-block' : 'q-d-none'
  }
}
export const getDiscount = (price: number | null, oldPrice: number | null): number | null => {
  if (!price || !oldPrice || isNaN(price) || isNaN(oldPrice) || oldPrice <= price) {
    return null;
  }
  
  return Math.floor(100 * (1 - price / oldPrice));
};

export const getRealPrice = (parsedPrice: number | null, parsedOldPrice: number | null) => {
  const isSwapped = parsedPrice && parsedOldPrice && parsedPrice > parsedOldPrice;
  return {
    price: isSwapped ? parsedOldPrice : parsedPrice,
    oldPrice: isSwapped ? parsedPrice : parsedOldPrice
  }
}


export async function fetchFrontSettings() {
  const myHeaders = new Headers();
  myHeaders.append("Api-key", config.apiKey);
  myHeaders.append("Accept", "application/vnd.qon+json;version=1.1");
  // myHeaders.append("Qon-cache-control", "no-cache");
  
  const requestOptions = {
    method: 'GET',
    headers: myHeaders,
    redirect: 'follow'
  };
  
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const response = await fetch(`https://${config.apiHost}/restapi/${config.customerSymbol}/search/${config.searchId}/frontSettings`, requestOptions)
  return await response.json()
}


export function searchIsConfigured() {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  searchLogger.log('Instance config: ', window?.SmartSearchConfig)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return window?.SmartSearchConfig?.apiKey && window?.SmartSearchConfig?.customerSymbol && window?.SmartSearchConfig?.searchId
}

export function deepAssign(options: { nonEnum: true, symbols: true, descriptors: true, proto: true } = {
  nonEnum: true,
  symbols: true,
  descriptors: true,
  proto: true
}) {
  function toType(a: object) {
    // Get fine type (object, array, function, null, error, date ...)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return ({}).toString.call(a).match(/([a-z]+)(:?\])/i)[1];
  }
  
  function isDeepObject(obj: object) {
    return "Object" === toType(obj);
  }
  
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return function deepAssignWithOptions(target, ...sources) {
    sources.forEach((source) => {
      
      if (!isDeepObject(source) || !isDeepObject(target))
        return;
      
      // Copy source's own properties into target's own properties
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      function copyProperty(property) {
        const descriptor = Object.getOwnPropertyDescriptor(source, property);
        //default: omit non-enumerable properties
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (descriptor.enumerable || options.nonEnum) {
          // Copy in-depth first
          if (isDeepObject(source[property]) && isDeepObject(target[property]))
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            descriptor.value = deepAssign(options)(target[property], source[property]);
          //default: omit descriptors
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (options.descriptors)
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Object.defineProperty(target, property, descriptor); // shallow copy descriptor
          else
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            target[property] = descriptor.value; // shallow copy value only
        }
      }
      
      // Copy string-keyed properties
      Object.getOwnPropertyNames(source).forEach(copyProperty);
      
      //default: omit symbol-keyed properties
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (options.symbols)
        Object.getOwnPropertySymbols(source).forEach(copyProperty);
      
      //default: omit prototype's own properties
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (options.proto)
        // Copy souce prototype's own properties into target prototype's own properties
        deepAssign(Object.assign({}, options, {proto: false}))(// Prevent deeper copy of the prototype chain
          Object.getPrototypeOf(target),
          Object.getPrototypeOf(source)
        );
      
    });
    return target;
  }
}

export function restoreDefaultSettings() {
  if (!sessionStorage.getItem('restoreDefaultSettings')) {
    return
  }
  searchErrorLogger.log('Replacing customComponent and redirectionUrl')
  
  SmartSearchConfig.value.appConfiguration.redirectionUrl = "/search/v3_app/index.html"
  // SmartSearchConfig.value.serp.customComponents = {
  //   inputElement: "",
  //   productCard: "",
  //   autocompleteSingleResult: "",
  //   singlePopularProduct: "",
  //   topProduct: ""
  // }
}
export function overrideSettings() {
  if (!localStorage.getItem('overrideSettings')) {
    return;
  }
  
  searchErrorLogger.log('OVERRIDE SETTING, DON\'T SAVE IT ON PRODUCTION!');
  
  if (!SmartSearchConfig || !SmartSearchConfig.value?.serp?.customComponents) {
    searchErrorLogger.log('SmartSearchConfig is not defined or improperly initialized');
    return;
  }
  
  const keys = [
    'overrideSettings_productCard',
    'overrideSettings_autocompleteSingleResult',
    'overrideSettings_singlePopularProduct',
    'overrideSettings_topProduct',
  ] as const;
  
  keys.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value) {
      const componentName = key.replace('overrideSettings_', '') as keyof CustomComponentsInterface;
      SmartSearchConfig.value.serp.customComponents[componentName] = value;
      searchErrorLogger.log(`OVERRIDE SETTING: ${key} replaced`);
    }
  });
}



export function showAsStylisationList() {
  if (!sessionStorage.getItem('showAsStylisationList')) {
    return
  }
  searchErrorLogger.log('Replacing customComponent and redirectionUrl')
  
  SmartSearchConfig.value.appConfiguration.redirectionUrl = "/search/v3_app/stylizations_list.html"
  SmartSearchConfig.value.appConfiguration.stylizationUrl = "https://cp2.quarticon.com/stylist/stylization"
  
  SmartSearchConfig.value.serp.customComponents = {
    inputElement: "",
    productCard: "",
    autocompleteSingleResult: "",
    singlePopularProduct: "",
    topProduct: ""
  }
}

export function encodeUTF8ToBase64(str: string): string {
  return btoa(unescape(encodeURIComponent(str)))
}

export function decodeUTF8FromBase64(base64: string): string {
  return decodeURIComponent(escape(atob(base64)))
}


